import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Breadcrumbs, P, Spinner } from "AbstractElements";
import { Card, CardBody, Col } from "reactstrap";
import Moment from "moment";
import {
	clearSessionDetail,
	fetchSessionProgressAnalyticsThunk,
	fetchSessionProgressDetailThunk,
	fetchSessionProgressSessionsThunk,
	setDateFilters,
	setSessionFilters,
} from "Features/Admin/SessionProgress/sessionProgressAnalyticsSlice";
import ProgressAverages from "./components/ProgressAverages";
import SessionsTable from "./components/SessionsTable";
import SessionDetailModal from "./components/SessionDetailModal";
import ProgressDateFilter from "./components/ProgressDateFilter";
import { formatDateParam, toApiDateFilters } from "./components/dateFilterUtils";

const toLocalDate = (value) => {
	if (!value) return null;
	const parsed = Moment(value, "YYYY-MM-DD", true);
	return parsed.isValid() ? parsed.toDate() : null;
};

const SessionProgressAnalytics = () => {
	const dispatch = useDispatch();
	const {
		analytics,
		sessions,
		pagination,
		sessionDetail,
		filters,
		isLoadingAnalytics,
		isLoadingSessions,
		isLoadingDetail,
		error,
		detailError,
	} = useSelector((state) => state.sessionProgressAnalytics, shallowEqual);

	const [isModalOpen, setIsModalOpen] = useState(false);

	const dateApiParams = useMemo(
		() =>
			toApiDateFilters({
				startDate: filters.start_date,
				endDate: filters.end_date,
			}),
		[filters.start_date, filters.end_date]
	);

	const sessionApiParams = useMemo(
		() => ({
			page: filters.page,
			per_page: filters.per_page,
			...dateApiParams,
		}),
		[filters.page, filters.per_page, dateApiParams]
	);

	useEffect(() => {
		dispatch(fetchSessionProgressAnalyticsThunk(dateApiParams));
	}, [dispatch, dateApiParams]);

	useEffect(() => {
		dispatch(fetchSessionProgressSessionsThunk(sessionApiParams));
	}, [dispatch, sessionApiParams]);

	const handlePageChange = useCallback(
		(page) => {
			dispatch(setSessionFilters({ page }));
		},
		[dispatch]
	);

	const handleDateFilterChange = useCallback(
		({ preset, year, startDate, endDate }) => {
			dispatch(
				setDateFilters({
					preset,
					year,
					start_date: formatDateParam(startDate),
					end_date: formatDateParam(endDate),
				})
			);
		},
		[dispatch]
	);

	const handleRowClick = useCallback(
		(row) => {
			if (!row?.id) return;
			setIsModalOpen(true);
			dispatch(fetchSessionProgressDetailThunk(row.id));
		},
		[dispatch]
	);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		dispatch(clearSessionDetail());
	}, [dispatch]);

	const isInitialLoading = isLoadingAnalytics && !analytics;
	const startDateValue = toLocalDate(filters.start_date);
	const endDateValue = toLocalDate(filters.end_date);
	const hasDateFilter = Boolean(filters.start_date || filters.end_date);

	return (
		<Fragment>
			<Breadcrumbs
				mainTitle="Private Session Progress"
				parent="Class"
				title="Private Session Progress"
			/>

			<Col lg="12">
				<Card
					className="border-0 shadow-sm overflow-hidden"
					style={{
						borderRadius: "1.5rem",
						background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
					}}
				>
					<CardBody className="p-3 p-md-4">
						<div className="mb-4">
							<h4 className="fw-semibold mb-1">Private Session Progress</h4>
							<P className="mb-0 text-muted">
								Analitik progress sesi private yang diisi tentor saat checkout (skala 1–5).
							</P>
						</div>

						<ProgressDateFilter
							preset={filters.preset || "all"}
							year={filters.year}
							startDate={startDateValue}
							endDate={endDateValue}
							onChange={handleDateFilterChange}
						/>

						{isInitialLoading && (
							<div className="loader-box">
								<Spinner attrSpinner={{ className: "loader-30" }} />
							</div>
						)}

						{error && (
							<div className="rounded-4 border border-danger-subtle bg-danger-subtle p-3 mb-3">
								<P className="mb-0 text-danger">
									{typeof error === "string" ? error : "Terjadi kesalahan saat memuat data."}
								</P>
							</div>
						)}

						{!isInitialLoading && analytics && (
							<>
								{analytics.totalSessions === 0 ? (
									<div className="rounded-4 border border-dashed bg-light p-4 text-center text-muted mb-4">
										Belum ada sesi private dengan session progress
										{hasDateFilter ? " pada periode yang dipilih." : "."}
									</div>
								) : (
									<ProgressAverages analytics={analytics} />
								)}

								<SessionsTable
									sessions={sessions}
									pagination={pagination}
									isLoading={isLoadingSessions}
									hasDateFilter={hasDateFilter}
									onRowClick={handleRowClick}
									onPageChange={handlePageChange}
								/>
							</>
						)}
					</CardBody>
				</Card>
			</Col>

			<SessionDetailModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				detail={sessionDetail}
				isLoading={isLoadingDetail}
				error={detailError}
			/>
		</Fragment>
	);
};

export default SessionProgressAnalytics;
